const API_KEY = process.env.REACT_APP_MAERSK_API_KEY;
const API_BASE_URL = 'https://api.maersk.com/schedules';
const SAECS_SERVICE_CODE = '278';
const VESSELS_PER_GRID = 8;

// Helper function to add delay between API calls
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to handle API rate limiting
const rateLimitedFetch = async (url, options, retryCount = 3) => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Consumer-Key': API_KEY,
        'Accept': 'application/json',
        ...options?.headers
      }
    });

    if (response.status === 429 && retryCount > 0) {
      console.log('Rate limit hit, waiting before retry...');
      await delay(1000); // Wait 1 second before retry
      return rateLimitedFetch(url, options, retryCount - 1);
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    if (retryCount > 0) {
      console.log(`Retrying... ${retryCount} attempts left`);
      await delay(1000);
      return rateLimitedFetch(url, options, retryCount - 1);
    }
    throw error;
  }
};

// Helper to get date range
const getDateRange = () => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 31); // 4 weeks back
  return {
    startDate: startDate.toISOString().split('T')[0],
    dateRange: 'P24W'  // Extended to 24 weeks (was P8W)
  };
};

// Helper function to check if a voyage number is valid (Northbound)
const isValidVoyage = (voyage) => {
  // Basic check: must exist and end with 'N'
  return voyage && typeof voyage === 'string' && voyage.endsWith('N');
};

// Helper function to get the latest SA departure event (PE, DUR, or CPT)
const getLatestSaDeparture = (saPortCalls) => {
  const saDeparturePorts = ['ZAPLZ', 'ZADUR', 'ZACPT'];
  let latestDep = null;

  if (!saPortCalls || !Array.isArray(saPortCalls)) return null; // Safety check

  for (const call of saPortCalls) {
    // Ensure call and necessary properties exist
    if (!call || !call.type || !call.port || !call.dateTime) continue;
    
    if (call.type === 'DEPA' && saDeparturePorts.includes(call.port)) {
      try {
        const currentDepTime = new Date(call.dateTime);
        if (isNaN(currentDepTime.getTime())) continue; // Skip invalid dates

        if (!latestDep) {
            latestDep = call;
        } else {
            const latestDepTime = new Date(latestDep.dateTime);
             if (isNaN(latestDepTime.getTime())) { // Handle if latestDep had invalid date
                latestDep = call;
                continue;
            }

            if (currentDepTime > latestDepTime) {
                latestDep = call;
            } else if (currentDepTime.getTime() === latestDepTime.getTime()) {
                // If times are equal, prefer Actual over Estimated
                if (call.status === 'ACT' && latestDep.status !== 'ACT') {
                latestDep = call;
                }
            }
        }
      } catch (e) {
        console.error("Error parsing date in getLatestSaDeparture:", call.dateTime, e);
        continue; // Skip this call if date parsing fails
      }
    }
  }
  return latestDep;
};

// Helper function to get admin vessel order (implementation depends on how you store it)
const getAdminVesselOrder = () => {
  try {
    const orderString = localStorage.getItem('saecsAdminOrder'); // Example: reading from localStorage
    return orderString ? JSON.parse(orderString) : null;
  } catch (e) {
    console.error("Error reading admin vessel order:", e);
    return null;
  }
};

// Main sorting function for SAECS vessels
const sortVesselsBySAPortDeparture = (vessels) => {
  // First filter out southbound voyages or invalid formats
  vessels = vessels.filter(v => v.vessel && isValidVoyage(v.vessel.voyage));

  // Define the default hardcoded order for 250N vessels (used as a TIE-BREAKER ONLY)
  const default250NOrder = [
    'SANTA ISABEL',
    'SANTA CLARA',
    'SANTA RITA',
    'ONE Resolution',
    'MEHUIN',
    'SANTA TERESA',
    'One Responsibility', // Corrected case
    'One Resilience'    // Corrected case
    // Add any other 250N vessels in their desired default order
  ];

  return vessels.sort((a, b) => {
    // --- Safety Check --- Ensure vessel objects and core properties exist
    if (!a || !a.vessel || !b || !b.vessel) return 0;
    const voyageA = a.vessel.voyage;
    const voyageB = b.vessel.voyage;
    const nameA = a.vessel.name;
    const nameB = b.vessel.name;
    if (!voyageA || !voyageB || !nameA || !nameB) {
        console.warn("Sorting vessels with missing data:", { voyageA, voyageB, nameA, nameB });
        return 0; // Avoid errors, treat as equal if core data missing
    }

    // Priority 1: Voyage Number (Numerical Part)
    const voyageNumA = parseInt(voyageA.match(/\d+/)?.[0] || 0);
    const voyageNumB = parseInt(voyageB.match(/\d+/)?.[0] || 0);
    if (voyageNumA !== voyageNumB) {
      return voyageNumA - voyageNumB; // Lower voyage number first
    }

    // Priority 2: Latest SA Departure (Status and Time)
    const depA = getLatestSaDeparture(a.saPortCalls);
    const depB = getLatestSaDeparture(b.saPortCalls);

    // Handle missing departure info - vessels with info come first
    if (depA && !depB) return -1; // a comes first
    if (!depA && depB) return 1;  // b comes first
    if (depA && depB) {
      // Both have departure info, compare them
      const isActualA = depA.status === 'ACT';
      const isActualB = depB.status === 'ACT';
      // Prefer Actual over Estimated
      if (isActualA !== isActualB) {
        return isActualA ? -1 : 1;
      }

      // Compare by time if status is the same
      try {
          const timeA = new Date(depA.dateTime);
          const timeB = new Date(depB.dateTime);
           // Add checks for Invalid Date
          const isTimeAValid = !isNaN(timeA.getTime());
          const isTimeBValid = !isNaN(timeB.getTime());

          if (isTimeAValid && !isTimeBValid) return -1; // Valid date comes first
          if (!isTimeAValid && isTimeBValid) return 1;
          if (isTimeAValid && isTimeBValid && timeA.getTime() !== timeB.getTime()) {
            return timeA - timeB; // Earlier date comes first
          }
      } catch (e) {
          console.error("Error comparing dates in sort:", depA.dateTime, depB.dateTime, e);
          // Fall through if date comparison fails
      }
    }
    // If we reach here, either both have no departure info, or their latest departures are identical/invalid

    // Priority 3: Special Rules (only apply if voyage numbers and dates are effectively the same)
    // Rule: SANTA CRUZ (251N) before ONE Resolution (251N)
    if (voyageA === '251N') { // No need to check voyageB as it must be same due to Priority 1
        if (nameA === 'SANTA CRUZ' && nameB === 'ONE Resolution') return -1;
        if (nameA === 'ONE Resolution' && nameB === 'SANTA CRUZ') return 1;
    }

    // Priority 4: Tie-breaker for 250N using hardcoded list (ONLY if dates are identical/missing)
    if (voyageA === '250N') { // No need to check voyageB
      const adminOrder = getAdminVesselOrder();
      if (adminOrder) {
        const indexA = adminOrder.indexOf(nameA);
        const indexB = adminOrder.indexOf(nameB);
        // Use admin order only if both vessels are found in it
        if (indexA !== -1 && indexB !== -1) {
          if (indexA !== indexB) return indexA - indexB; // Use Admin Order
          // If indexes are the same, fall through to next tie-breaker
        }
         // If one or both not in admin order, fall through
      }

      // Fallback to Default Hardcoded 250N Order if Admin order didn't apply or resulted in a tie
      const defaultIndexA = default250NOrder.indexOf(nameA);
      const defaultIndexB = default250NOrder.indexOf(nameB);
       // Use default order only if both vessels are found in it
      if (defaultIndexA !== -1 && defaultIndexB !== -1) {
          if (defaultIndexA !== defaultIndexB) return defaultIndexA - defaultIndexB; // Use Default 250N Order
          // If indexes are the same, fall through
      }
      // If one or both not in default order, fall through
    }

    // Priority 5: Final Tie-breaker - Alphabetical by Name
    if (nameA !== nameB) {
      return nameA.localeCompare(nameB);
    }

    return 0; // Should be identical if we reach here
  });
};

// Enhanced function to fetch discharge schedules for multiple vessels
const fetchDischargeSchedules = async (vessels, dateParams) => {
  const schedules = {};
  
  // Process vessels sequentially to respect rate limits
  for (const vessel of vessels) {
    try {
      console.log(`Fetching schedule for vessel: ${vessel.name} (${vessel.vesselIMO})`);
      const response = await rateLimitedFetch(
        `${API_BASE_URL}/vessel-schedules?vesselIMONumber=${vessel.vesselIMO}&carrierCodes=MAEU&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`
      );
      
      if (response) {
        schedules[vessel.name] = response;
      }
      
      // Add small delay between requests to help prevent rate limiting
      await delay(200);
    } catch (error) {
      console.error(`Error fetching schedule for vessel ${vessel.name}:`, error);
      schedules[vessel.name] = null;
    }
  }
  
  return schedules;
};

const fetchSAECSSchedules = async () => {
  try {
    // Get date range for all API calls
    const dateParams = getDateRange();
    
    // Fetch all SA Load Port data concurrently
    const [capeTownData, durbanData, peData] = await Promise.all([
      rateLimitedFetch(
        `${API_BASE_URL}/port-calls?countryCode=ZA&cityName=Cape Town&carrierCodes=MAEU&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`
      ),
      rateLimitedFetch(
        `${API_BASE_URL}/port-calls?countryCode=ZA&cityName=Durban&carrierCodes=MAEU&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`
      ),
      rateLimitedFetch(
        `${API_BASE_URL}/port-calls?countryCode=ZA&cityName=Port Elizabeth&carrierCodes=MAEU&startDate=${dateParams.startDate}&dateRange=${dateParams.dateRange}`
      )
    ]);
    
    // --- Step 1: Consolidate unique vessels and their SA port calls ---
    const allSAPortData = {
      ZACPT: capeTownData,
      ZADUR: durbanData,
      ZAPLZ: peData
    };
    const vesselMap = new Map();

    Object.entries(allSAPortData).forEach(([portCode, portData]) => {
      if (!portData?.portCalls) return;

      portData.portCalls.forEach(portCall => {
        portCall.facilityCalls.forEach(facilityCall => {
          const transport = facilityCall.transport;
          const serviceCode = transport?.outboundService?.carrierServiceCode || transport?.inboundService?.carrierServiceCode;
          const voyageNumber = transport?.outboundService?.carrierVoyageNumber || transport?.inboundService?.carrierVoyageNumber;
          const vesselName = transport?.vessel?.vesselName;

          if (serviceCode === SAECS_SERVICE_CODE && vesselName && voyageNumber) {
            const vesselKey = `${vesselName}-${voyageNumber}`;

            if (!vesselMap.has(vesselKey)) {
              vesselMap.set(vesselKey, {
                vessel: {
                  name: vesselName,
                  voyage: voyageNumber,
                  code: transport.vessel.carrierVesselCode,
                  vesselIMO: transport.vessel.vesselIMONumber
                },
                saPortCalls: []
              });
            }

            const vesselEntry = vesselMap.get(vesselKey);
            facilityCall.callSchedules.forEach(schedule => {
              if (['ARRI', 'DEPA'].includes(schedule.transportEventTypeCode)) {
                vesselEntry.saPortCalls.push({
                  port: portCode,
                  facility: facilityCall.facility?.locationName || portCode,
                  type: schedule.transportEventTypeCode,
                  status: schedule.eventClassifierCode,
                  dateTime: schedule.classifierDateTime || schedule.dateTime,
                  transportCallSequence: facilityCall.transportCallSequence
                });
              }
            });

            vesselEntry.saPortCalls.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
          }
        });
      });
    });

    const consolidatedVessels = Array.from(vesselMap.values());

    // --- Step 2: Sort vessels based on latest SA departure ---
    const sortedVessels = sortVesselsBySAPortDeparture(consolidatedVessels);

    // --- Step 3: Split into current and upcoming rotations ---
    const currentRotation = sortedVessels.slice(0, VESSELS_PER_GRID);
    const upcomingRotation = sortedVessels.slice(VESSELS_PER_GRID, VESSELS_PER_GRID * 2);

    // --- Step 4: Process port schedules for both rotations ---
    const processRotationSchedules = (rotation, portData) => {
      return rotation.map(vesselData => {
        const portCalls = vesselData.saPortCalls.filter(call => call.port === portData.portCode);
        const arrival = portCalls.find(call => call.type === 'ARRI');
        const departure = portCalls.find(call => call.type === 'DEPA');

        return {
          eta: formatDate(arrival?.dateTime, arrival?.status),
          etd: formatDate(departure?.dateTime, departure?.status)
        };
      });
    };

    // Process current rotation port schedules
    const currentLoadPorts = [
      { name: 'Port Elizabeth', portCode: 'ZAPLZ', schedules: processRotationSchedules(currentRotation, { portCode: 'ZAPLZ' }) },
      { name: 'Durban', portCode: 'ZADUR', schedules: processRotationSchedules(currentRotation, { portCode: 'ZADUR' }) },
      { name: 'Cape Town', portCode: 'ZACPT', schedules: processRotationSchedules(currentRotation, { portCode: 'ZACPT' }) }
    ];

    // Process upcoming rotation port schedules
    const upcomingLoadPorts = [
      { name: 'Port Elizabeth', portCode: 'ZAPLZ', schedules: processRotationSchedules(upcomingRotation, { portCode: 'ZAPLZ' }) },
      { name: 'Durban', portCode: 'ZADUR', schedules: processRotationSchedules(upcomingRotation, { portCode: 'ZADUR' }) },
      { name: 'Cape Town', portCode: 'ZACPT', schedules: processRotationSchedules(upcomingRotation, { portCode: 'ZACPT' }) }
    ];

    // --- Step 5: Fetch and process discharge schedules ---
    const allVessels = [...currentRotation, ...upcomingRotation];
    const dischargeSchedules = await fetchDischargeSchedules(allVessels.map(v => v.vessel), dateParams);

    let dischargePortsConfig = [
      { key: 'LGP', name: 'London Gateway', code: 'GBLGP' },
      { key: 'RTM', name: 'Rotterdam', code: 'NLRTM' },
      { key: 'BRV', name: 'Bremerhaven', code: 'DEBRV' },
      { key: 'ALG-ML', name: 'Algeciras - ML', code: 'ESALG', facilityName: 'Algeciras - ML Terminal' },
      { key: 'ALG-TTI', name: 'Algeciras - TTI', code: 'ESALG', facilityName: 'Algeciras - TTI' },
    ];

    // Filter out 'Algeciras - TTI'
    dischargePortsConfig = dischargePortsConfig.filter(p => p.name !== 'Algeciras - TTI');

    // Sort to ensure 'Algeciras - ML' is first among discharge ports
    dischargePortsConfig.sort((a, b) => {
      if (a.name === 'Algeciras - ML') return -1;
      if (b.name === 'Algeciras - ML') return 1;
      return 0;
    });

    const currentDischargePorts = dischargePortsConfig.map(port => ({
      name: port.name,
      schedules: currentRotation.map(vesselData => {
        const vesselSchedule = dischargeSchedules[vesselData.vessel.name];
        return processDischargeSchedule(vesselSchedule, port.facilityName || port.name, vesselData.vessel);
      })
    }));

    const upcomingDischargePorts = dischargePortsConfig.map(port => ({
      name: port.name,
      schedules: upcomingRotation.map(vesselData => {
        const vesselSchedule = dischargeSchedules[vesselData.vessel.name];
        return processDischargeSchedule(vesselSchedule, port.facilityName || port.name, vesselData.vessel);
      })
    }));

    return {
      current: {
        vessels: currentRotation.map(v => v.vessel),
        loadPorts: currentLoadPorts,
        dischargePorts: currentDischargePorts
      },
      upcoming: {
        vessels: upcomingRotation.map(v => v.vessel),
        loadPorts: upcomingLoadPorts,
        dischargePorts: upcomingDischargePorts
      }
    };

  } catch (error) {
    console.error('Error in fetchSAECSSchedules:', error);
    throw error;
  }
};

// Helper function to process discharge schedule
const processDischargeSchedule = (dischargeData, portName, vesselData) => {
  if (!dischargeData?.vesselCalls) {
    return { eta: { text: 'N/A', type: 'EST', rawDateTime: null }, etd: { text: 'N/A', type: 'EST', rawDateTime: null } };
  }
  
  // Find the correct port call that matches both port and voyage number
  const call = dischargeData.vesselCalls.find(call => {
    // First check if this is the right port
    const isCorrectPort = portName.includes('Algeciras') 
      ? (portName.includes('ML Terminal') 
          ? !call.facility.locationName.includes('TTI') 
          : call.facility.locationName.includes('TTI'))
        && call.facility.cityName === 'Algeciras'
      : call.facility.cityName === portName;

    if (!isCorrectPort) return false;

    // Then check if this matches our export voyage number
    const matchesVoyage = 
      call.transport?.outboundService?.carrierVoyageNumber === vesselData.voyage ||
      call.transport?.inboundService?.carrierVoyageNumber === vesselData.voyage;

    return isCorrectPort && matchesVoyage;
  });

  if (!call) {
    return { eta: { text: 'N/A', type: 'EST', rawDateTime: null }, etd: { text: 'N/A', type: 'EST', rawDateTime: null } };
  }

  const arrival = call.callSchedules.find(s => s.transportEventTypeCode === 'ARRI');
  const departure = call.callSchedules.find(s => s.transportEventTypeCode === 'DEPA');

  // Check if this is the last discharge port (London Gateway)
  const isLastDischargePort = portName === 'London Gateway';
  
  // If this is the last discharge port and we have an actual arrival
  if (isLastDischargePort && arrival?.eventClassifierCode === 'ACT') {
    const arrivalDate = new Date(arrival.classifierDateTime);
    const oneDayAfter = new Date(arrivalDate);
    oneDayAfter.setDate(oneDayAfter.getDate() + 1);
    
    // If it's been less than a day since actual arrival, keep the vessel
    if (new Date() < oneDayAfter) {
      vesselData.keepVisible = true;
    }
  }

  return {
    eta: formatDate(arrival?.dateTime || arrival?.classifierDateTime, arrival?.eventClassifierCode),
    etd: formatDate(departure?.dateTime || departure?.classifierDateTime, departure?.eventClassifierCode)
  };
};

// Date formatting helper
const formatDate = (dateTimeStr, eventClassifierCode) => {
  console.log(`[formatDate] Input dateTimeStr: ${dateTimeStr}, eventClassifierCode: ${eventClassifierCode}`); // Added log
  if (!dateTimeStr || dateTimeStr === 'N/A' || dateTimeStr === 'TBD') { // Added checks for N/A, TBD
    console.log(`[formatDate] Returning N/A`); // Added log
    return { text: 'N/A', type: 'EST', rawDateTime: null };
  }
  try {
    const date = new Date(dateTimeStr);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
        console.warn(`[formatDate] Invalid Date created from string: ${dateTimeStr}`); // Added log
        return { text: 'Invalid Date', type: 'EST', rawDateTime: dateTimeStr };
    }
    const result = {
      text: date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        // year: 'numeric' // Temporarily remove year for brevity if needed
      }),
      rawDateTime: dateTimeStr,
      type: eventClassifierCode === 'ACT' ? 'ACT' : 'EST'
    };
    console.log(`[formatDate] Output:`, JSON.stringify(result)); // Added log
    return result;
  } catch (error) {
    console.error(`[formatDate] Error formatting date string: ${dateTimeStr}`, error); // Added log
    return { text: 'Error', type: 'EST', rawDateTime: dateTimeStr };
  }
};

// Normalize vessel name
const normalizeVesselName = (name) => {
    if (!name) return '';
    
    // Remove any whitespace and convert to lowercase
    name = name.trim().toLowerCase();
    
    // Remove common prefixes and suffixes
    const prefixes = ['mv', 'mv.', 'ss', 'ss.'];
    for (const prefix of prefixes) {
        if (name.startsWith(prefix + ' ')) {
            name = name.slice(prefix.length).trim();
        }
    }
    
    // Remove special characters and extra spaces
    name = name.replace(/[^a-z0-9]/g, '');
    
    // Common carrier abbreviations
    const carrierMappings = {
        'maersk': 'msk',
        'mediterranean shipping company': 'msc',
        'cma cgm': 'cmacgm'
    };
    
    // Apply carrier mappings
    for (const [full, abbrev] of Object.entries(carrierMappings)) {
        name = name.replace(full, abbrev);
    }
    
    return name;
};

// Normalize voyage number
const normalizeVoyage = (voyage) => {
    if (!voyage) return '';
    
    // Remove any whitespace and convert to uppercase
    voyage = voyage.trim().toUpperCase();
    
    // Handle multiple voyages (e.g., "NZ444A NZ449R" -> "NZ449R")
    if (voyage.includes(' ')) {
        const voyages = voyage.split(' ');
        // Take the last voyage as it's typically the export voyage
        voyage = voyages[voyages.length - 1];
    }
    
    // Handle split voyages with slashes first (e.g., "445S/449N" -> "449N")
    if (voyage.includes('/')) {
        // Special cases: don't split if it's just a suffix like S/N or W/E
        if (!voyage.endsWith('/N') && !voyage.endsWith('/E')) {
            const voyages = voyage.split('/');
            // Take the last part as it's typically the export voyage
            voyage = voyages[voyages.length - 1];
        }
    }
    
    // Now handle suffix variations
    voyage = voyage
        .replace('S/N', 'N')
        .replace('S/', 'N')
        .replace('N/S', 'N')
        .replace('W/E', 'E')
        .replace('W/', 'E');
    
    // Remove any remaining special characters
    voyage = voyage.replace(/[^A-Z0-9]/g, '');
    
    // Handle common suffix conversions
    const suffixMappings = {
        'S': 'N',  // Convert S to N for northbound
        'W': 'E'   // Convert W to E as per example
    };
    
    // Check if voyage ends with any of the suffixes and convert
    for (const [from, to] of Object.entries(suffixMappings)) {
        if (voyage.endsWith(from)) {
            voyage = voyage.slice(0, -1) + to;
        }
    }
    
    // If it's just a number, append N (default to northbound)
    if (/^\d+$/.test(voyage)) {
        voyage = voyage + 'N';
    }
    
    return voyage;
};

// Cache for stack dates
// let stackDatesCache = null;
// let lastFetchTime = null;
// const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fetch all stack dates from the JSON file
export const fetchAllStackDates = async () => {
    try {
        const response = await fetch('/stack_dates.json');
        const json = await response.json();
        return json.data || [];
    } catch (error) {
        console.error('Error loading stack dates:', error);
        return [];
    }
};

// Fetch stack dates for a vessel
export const fetchStackDates = async (vesselName, voyage) => {
    try {
        console.log('Fetching stack dates for:', { vesselName, voyage });
        
        // Get all stack dates
        const allDates = await fetchAllStackDates();
        if (!allDates || allDates.length === 0) {
            console.log('No stack dates available');
            return [];
        }
        
        // Normalize search terms
        const normalizedVessel = normalizeVesselName(vesselName);
        const normalizedVoyage = normalizeVoyage(voyage);
        
        console.log('Normalized search terms:', {
            normalizedVessel,
            normalizedVoyage,
            originalVessel: vesselName,
            originalVoyage: voyage
        });
        
        // Find exact matches first
        const exactMatches = allDates.filter(date => {
            if (!date || !date.vessel_name || !date.voyage) {
                console.log('Invalid date entry:', date);
                return false;
            }
            
            const dateNormalizedVessel = normalizeVesselName(date.vessel_name);
            const dateNormalizedVoyage = normalizeVoyage(date.voyage);
            
            console.log('Comparing with:', {
                dateVessel: date.vessel_name,
                dateVoyage: date.voyage,
                normalizedDateVessel: dateNormalizedVessel,
                normalizedDateVoyage: dateNormalizedVoyage
            });
            
            return dateNormalizedVessel === normalizedVessel && 
                   dateNormalizedVoyage === normalizedVoyage;
        });
        
        // If we have exact matches, return those
        if (exactMatches.length > 0) {
            console.log('Found exact matches:', exactMatches);
            return exactMatches;
        }
        
        // Try fuzzy matching with more lenient criteria
        const fuzzyMatches = allDates.filter(date => {
            if (!date || !date.vessel_name || !date.voyage) {
                return false;
            }
            
            const dateNormalizedVessel = normalizeVesselName(date.vessel_name);
            const dateNormalizedVoyage = normalizeVoyage(date.voyage);
            
            // For vessel similarity:
            // 1. Check if one contains the other
            // 2. Check if they share a significant portion of characters
            const vesselSimilar = (
                dateNormalizedVessel.includes(normalizedVessel) || 
                normalizedVessel.includes(dateNormalizedVessel) ||
                calculateSimilarity(dateNormalizedVessel, normalizedVessel) > 0.8
            );
            
            // For voyage similarity:
            // 1. Check if numeric parts match
            // 2. Check if they're exactly equal
            const numericVoyage = normalizedVoyage.replace(/[^0-9]/g, '');
            const numericDateVoyage = dateNormalizedVoyage.replace(/[^0-9]/g, '');
            const voyageSimilar = (
                numericVoyage === numericDateVoyage ||
                dateNormalizedVoyage === normalizedVoyage
            );
            
            const isMatch = vesselSimilar && voyageSimilar;
            
            if (isMatch) {
                console.log('Found fuzzy match:', {
                    stackVessel: date.vessel_name,
                    normalizedStackVessel: dateNormalizedVessel,
                    stackVoyage: date.voyage,
                    normalizedStackVoyage: dateNormalizedVoyage,
                    similarity: calculateSimilarity(dateNormalizedVessel, normalizedVessel)
                });
            }
            
            return isMatch;
        });
        
        console.log('Final matches:', fuzzyMatches);
        
        // Sort by stack opening date
        return fuzzyMatches.sort((a, b) => 
            new Date(b.stack_opening) - new Date(a.stack_opening)
        );
    } catch (error) {
        console.error('Error fetching stack dates:', error);
        return null;
    }
};

// Helper function to calculate string similarity (Levenshtein distance)
const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const costs = [];
    for (let i = 0; i <= longer.length; i++) {
        let lastValue = i;
        for (let j = 0; j <= shorter.length; j++) {
            if (i === 0) {
                costs[j] = j;
            } else if (j > 0) {
                let newValue = costs[j - 1];
                if (longer[i - 1] !== shorter[j - 1]) {
                    newValue = Math.min(
                        Math.min(newValue, lastValue),
                        costs[j]
                    ) + 1;
                }
                costs[j - 1] = lastValue;
                lastValue = newValue;
            }
        }
        if (i > 0) costs[shorter.length] = lastValue;
    }
    
    return (longer.length - costs[shorter.length]) / longer.length;
};

// Test function to verify vessel and voyage matching
const testStackDateMatching = async () => {
    const testCases = [
        { vessel: 'MSC Clarita III', voyage: 'ZA449A' },
        { vessel: 'Santa Cruz', voyage: '244S/N' },
        { vessel: 'MSC Yashi B', voyage: 'NZ444A NZ449R' },
        { vessel: 'COSCO Aqabqa', voyage: '084W 084E' },
        { vessel: 'Santa Isabel', voyage: '244S/N' },
        { vessel: 'Nele Maersk', voyage: '445S/449N' },
        { vessel: 'CMA CGM Davao', voyage: '0WY2FS1MA 0WY2GN1MA' },
        { vessel: 'Ital Unica', voyage: '181W/E' },
        { vessel: 'MSC Krystal', voyage: 'NZ450R' },
        { vessel: 'Maersk Cap Carmel', voyage: '450E/452W' }
    ];

    // First fetch all stack dates once
    const allDates = await fetchAllStackDates();
    console.log('Total stack dates in database:', allDates.length);
    
    console.log('\n=== Starting Matching Tests ===\n');
    
    for (const testCase of testCases) {
        console.log(`\n🚢 Testing vessel: ${testCase.vessel}`);
        console.log(`📝 Voyage: ${testCase.voyage}`);
        
        const normalizedVessel = normalizeVesselName(testCase.vessel);
        const normalizedVoyage = normalizeVoyage(testCase.voyage);
        
        console.log('Normalized to:', {
            vessel: normalizedVessel,
            voyage: normalizedVoyage
        });
        
        // Find matches in the already fetched stack dates
        const matches = allDates.filter(date => {
            const dateNormalizedVessel = normalizeVesselName(date.vessel_name);
            const dateNormalizedVoyage = normalizeVoyage(date.voyage);
            
            const vesselMatch = dateNormalizedVessel === normalizedVessel;
            const voyageMatch = dateNormalizedVoyage === normalizedVoyage;
            
            if (vesselMatch || voyageMatch) {
                console.log('Potential match found:', {
                    stackVessel: date.vessel_name,
                    normalizedStackVessel: dateNormalizedVessel,
                    stackVoyage: date.voyage,
                    normalizedStackVoyage: dateNormalizedVoyage,
                    matched: vesselMatch ? 'vessel' : 'voyage'
                });
            }
            
            return vesselMatch && voyageMatch;
        });
        
        if (matches.length > 0) {
            console.log('✅ MATCH FOUND!');
            matches.forEach((match, index) => {
                console.log(`Match ${index + 1}:`, {
                    vessel: match.vessel_name,
                    voyage: match.voyage,
                    stack_opening: match.stack_opening,
                    stack_closing: match.stack_closing,
                    terminal: match.terminal
                });
            });
        } else {
            console.log('❌ NO MATCH FOUND');
        }
    }
};

// Export for testing
export { testStackDateMatching };

// --- Admin Vessel Order Management (localStorage) ---
const VESSEL_ORDER_KEY = 'adminVesselOrder_250N';

// Helper function to set admin vessel order (implementation depends on how you store it)
const setAdminVesselOrder = (vessels) => {
  if (typeof localStorage === 'undefined') return false; // Check if localStorage is available
  try {
    const vesselNames = vessels.map(v => v.name);
    localStorage.setItem(VESSEL_ORDER_KEY, JSON.stringify(vesselNames));
    return true;
  } catch (e) {
    console.error("Error setting admin vessel order:", e);
    return false;
  }
};

const SCHEDULE_BASE_URL = process.env.REACT_APP_SCHEDULE_BASE_URL;

/**
 * Fetches port calls based on port identifiers, carrier codes, and date range.
 * @param {string} countryCode - ISO 3166-1 country code.
 * @param {string} cityName - Name of the city for the port.
 * @param {string[]} carrierCodes - Array of SCAC codes (e.g., ['MAEU']).
 * @param {string} startDate - Start date for the schedule (YYYY-MM-DD).
 * @param {string} dateRange - Date range for the schedule (e.g., P7D).
 * @param {string} [portUNLocationCode] - Optional UN Location Code for the port.
 * @returns {Promise<object>} The API response data.
 */
const fetchPortCalls = async ({ countryCode, cityName, portUNLocationCode, carrierCodes, startDate, dateRange }) => {
  // Validate essential parameters
  if (!countryCode || !cityName || !carrierCodes || carrierCodes.length === 0 || !startDate || !dateRange) {
    console.error('fetchPortCalls: Missing required parameters.', { countryCode, cityName, portUNLocationCode, carrierCodes, startDate, dateRange });
    throw new Error('Required parameters are missing for fetching port calls.');
  }

  // Construct query parameters
  const params = new URLSearchParams();
  params.append('countryCode', countryCode);
  params.append('cityName', cityName);
  if (portUNLocationCode) {
    params.append('UNLocationCode', portUNLocationCode);
  }
  // Append each carrier code separately
  carrierCodes.forEach(code => params.append('carrierCodes', code));
  params.append('startDate', startDate);
  params.append('dateRange', dateRange);

  // Construct the final URL
  const url = `${SCHEDULE_BASE_URL}/port-calls?${params.toString()}`;
  console.log('Constructed Port Calls URL:', url); // Log the constructed URL for debugging

  try {
    const response = await rateLimitedFetch(url);
    return response;
  } catch (error) {
    console.error('Error fetching port calls:', error);
    throw error;
  }
};

export { fetchSAECSSchedules, fetchPortCalls };

/* TODO: Potential Enhancements
 * - More robust error handling for API calls.
 * - Implement cancellation tokens for fetch requests.
 * - Consider a state management library (like Redux or Zustand) for complex state.
 * - Add unit tests for helper functions (e.g., normalizeVesselName, formatDate).
 */